// Core
import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Components
import { FieldSelect, RemoteTable } from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Redux
import { useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";

// Utils
import { datetimeToDate, datetimeToTime } from "utils/time";
import { useGetResources } from "utils/crud/read";
import { resourceVisual } from "utils/resourceTypes";

/* Generic server-paginated loads table (battery/EV/EWH/HP measurements).
   `query` is the RTK Query hook — fixed per instance, so calling it inside is
   hook-safe. The endpoints share the same contract:
   {data, total_count, distinct_dates, distinct_times} with
   {page, limit, date, time, resourceId, communityId, sortColumn, sortDirection}. */
function LoadsTableSection({
  query,
  resourceField,
  resourceLabel,
  resourceType,
  valueLabel,
  valueDigits,
  valueRender,
}) {
  const community = useSelector(selectCurrentCommunity);
  const resources = useGetResources("true");
  const { t } = useTranslation(["internalLoads", "common"]);

  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const [sortOrder, setSortOrder] = useState(undefined);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [resource, setResource] = useState(null);

  const { data, isFetching } = query(
    {
      page,
      limit,
      date,
      time,
      resourceId: resource,
      communityId: community.id,
      sortColumn: sortOrder?.column,
      sortDirection: sortOrder?.direction,
    },
    {
      pollingInterval: 15000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    }
  );

  const resourceOptions = useMemo(
    () =>
      (resources ?? [])
        .filter((r) => !resourceType || r.type === resourceType)
        .map((r) => ({ value: r.id, label: r.name })),
    [resources, resourceType]
  );
  const resourceIdToName = (resourceId) => resources?.find((r) => r.id === resourceId)?.name ?? "—";

  const rows = useMemo(
    () =>
      (data?.data ?? []).map((row, i) => ({
        id: row.id ?? i,
        date: datetimeToDate(row.datetime_timestamp),
        time: datetimeToTime(row.datetime_timestamp),
        resource: row[resourceField] ? resourceIdToName(row[resourceField]) : "—",
        resourceType: resources?.find((r) => r.id === row[resourceField])?.type,
        value: row[`${valueLabel.field}`],
      })),
    [data, resources, resourceField, valueLabel.field]
  );

  const columns = [
    { key: "date", label: t("table.columns.date"), sortable: true },
    { key: "time", label: t("table.columns.time"), sortable: true },
    {
      key: "resource",
      label: resourceLabel,
      render: (v, row) => {
        const visual = resourceVisual(row.resourceType || resourceType);
        return (
          <div className="rec-cell-member">
            <div className={`rec-av-sm ${visual.tint}`}>
              <Icon name={visual.icon} style={{ fontSize: 18 }} />
            </div>
            <b>{v}</b>
          </div>
        );
      },
    },
    {
      key: "value",
      label: valueLabel.label,
      align: "right",
      render: (v) => (
        <span className="tabular-nums">
          {valueRender ? valueRender(v) : (v != null && Number(v).toFixed(valueDigits)) || "—"}
        </span>
      ),
    },
  ];

  const onFilter = (setter) => (v) => {
    setPage(0);
    setter(v || null);
  };

  const dateOptions = [
    { value: "", label: t("filters.all_dates", { defaultValue: "Todas as datas" }) },
    ...(data?.distinct_dates ?? []).map((o) => ({ value: o.date, label: o.date })),
  ];
  const timeOptions = [
    { value: "", label: t("filters.all_times", { defaultValue: "Todas as horas" }) },
    ...(data?.distinct_times ?? []).map((o) => ({ value: o.time, label: o.time })),
  ];

  return (
    <div className="rec-card">
      <RemoteTable
        columns={columns}
        rows={rows}
        totalCount={data?.total_count ?? 0}
        page={page}
        limit={limit}
        onPageChange={setPage}
        sort={sortOrder}
        onSortChange={(s) => {
          setPage(0);
          setSortOrder(s);
        }}
        loading={isFetching}
        filters={
          <>
            <div className="min-w-[170px]">
              <FieldSelect value={date ?? ""} options={dateOptions} onChange={onFilter(setDate)} />
            </div>
            <div className="min-w-[150px]">
              <FieldSelect value={time ?? ""} options={timeOptions} onChange={onFilter(setTime)} />
            </div>
            <div className="min-w-[200px]">
              <FieldSelect
                value={resource ?? ""}
                options={[
                  { value: "", label: t("filters.all_resources", { defaultValue: "Todos" }) },
                  ...resourceOptions,
                ]}
                onChange={onFilter(setResource)}
              />
            </div>
          </>
        }
      />
    </div>
  );
}

LoadsTableSection.propTypes = {
  query: PropTypes.func.isRequired,
  resourceField: PropTypes.string.isRequired,
  resourceLabel: PropTypes.node.isRequired,
  resourceType: PropTypes.string,
  valueLabel: PropTypes.shape({
    field: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
  }).isRequired,
  valueDigits: PropTypes.number,
  valueRender: PropTypes.func,
};

LoadsTableSection.defaultProps = {
  resourceType: undefined,
  valueDigits: 2,
  valueRender: undefined,
};

export default LoadsTableSection;
